import React, { useEffect, useState } from 'react';
import { listPaymentMethods } from '@/services/api';
import { parseHostedError } from '@/services/errors';
import { paymentMethodLabel } from '@/format';
import NewCardElement from './NewCardElement';
import Spinner from '@/components/Spinner';
import ErrorBanner from '@/components/ErrorBanner';

/**
 * The "Payment method" block (Figma Web·2 / 2a): the default-card chip or a
 * dashed "Add a card" launcher when there's none; expanding reveals the
 * saved-method radio list + an embedded Stripe PaymentElement for a new card.
 *
 * Reports the parent's choice upward via onSelectionChange:
 *   {type:'default'} · {type:'saved', pmId, isDefault} · {type:'new'}
 * The new-card confirm function arrives via onBridge (see NewCardElement).
 */
const PaymentMethodSection = ({
	studentId,
	stripeAccountId,
	defaultPm,
	onSelectionChange,
	onBridge,
	forceExpanded,
}) => {
	const [expanded, setExpanded] = useState(forceExpanded || !defaultPm);
	const [methods, setMethods] = useState(null);
	const [error, setError] = useState(null);
	const [selected, setSelected] = useState(defaultPm ? 'default' : 'new');

	useEffect(() => {
		if (forceExpanded) setExpanded(true);
	}, [forceExpanded]);

	useEffect(() => {
		if (!expanded || methods) return;
		listPaymentMethods(studentId)
			.then((list) => setMethods(Array.isArray(list) ? list : []))
			.catch((err) => setError(parseHostedError(err).message));
	}, [expanded, methods, studentId]);

	const select = (value) => {
		setSelected(value);
		if (value === 'new') {
			onSelectionChange({ type: 'new' });
		} else if (value === 'default') {
			onSelectionChange({ type: 'default' });
		} else {
			const pm = (methods ?? []).find((m) => m.id === value);
			onSelectionChange({ type: 'saved', pmId: value, isDefault: Boolean(pm?.is_default) });
		}
	};

	if (!expanded) {
		return (
			<div className="flex w-full flex-col gap-[8px]">
				<p className="text-[13px] font-semibold text-[#16181d]">Payment method</p>
				{defaultPm ? (
					<div className="flex h-[56px] w-full items-center justify-between rounded-[12px] border-[1.5px] border-[#d5d7da] bg-white px-[16px]">
						<p className="text-[15px] font-semibold text-[#16181d]">{paymentMethodLabel(defaultPm)}</p>
						<button
							type="button"
							onClick={() => setExpanded(true)}
							className="text-[13px] font-semibold text-[#028afd]"
						>
							Change
						</button>
					</div>
				) : (
					<button
						type="button"
						onClick={() => {
							setExpanded(true);
							select('new');
						}}
						className="flex h-[56px] w-full items-center justify-between rounded-[12px] border-[1.5px] border-dashed border-[#028afd] bg-white px-[16px]"
					>
						<span className="flex items-center gap-[8px] text-[#028afd]">
							<span className="text-[18px] font-bold">＋</span>
							<span className="text-[15px] font-semibold">Add a payment method</span>
						</span>
						<span className="text-[12px] font-medium text-[#7d7d7d]">🔒 via Stripe</span>
					</button>
				)}
				<p className="text-[12px] text-[#7d7d7d]">You’ll enter your payment details securely on Stripe.</p>
			</div>
		);
	}

	return (
		<div className="flex w-full flex-col gap-[8px]">
			<p className="text-[13px] font-semibold text-[#16181d]">Payment method</p>

			{error ? <ErrorBanner>{error}</ErrorBanner> : null}
			{!methods && !error ? <Spinner label="Loading payment methods…" /> : null}

			{(methods ?? []).map((pm) => (
				<label
					key={pm.id}
					className={`flex h-[52px] w-full cursor-pointer items-center gap-[12px] rounded-[12px] border-[1.5px] bg-white px-[16px] ${
						selected === pm.id ? 'border-[#676AF6]' : 'border-[#d5d7da]'
					}`}
				>
					<input
						type="radio"
						name="pay-pm"
						checked={selected === pm.id}
						onChange={() => select(pm.id)}
						className="h-[16px] w-[16px] accent-[#676AF6]"
					/>
					<span className="flex-1 text-[15px] font-semibold text-[#16181d]">{paymentMethodLabel(pm)}</span>
					{pm.exp_month ? (
						<span className="text-[13px] text-[#7d7d7d]">
							{String(pm.exp_month).padStart(2, '0')}/{String(pm.exp_year).slice(-2)}
						</span>
					) : null}
					{pm.is_default ? <span className="text-[12px] font-medium text-[#028afd]">Default</span> : null}
				</label>
			))}

			<label
				className={`flex h-[52px] w-full cursor-pointer items-center gap-[12px] rounded-[12px] border-[1.5px] bg-white px-[16px] ${
					selected === 'new' ? 'border-[#676AF6]' : 'border-dashed border-[#028afd]'
				}`}
			>
				<input
					type="radio"
					name="pay-pm"
					checked={selected === 'new'}
					onChange={() => select('new')}
					className="h-[16px] w-[16px] accent-[#676AF6]"
				/>
				<span className="flex-1 text-[15px] font-semibold text-[#028afd]">＋ Add a new payment method</span>
				<span className="text-[12px] font-medium text-[#7d7d7d]">🔒 via Stripe</span>
			</label>

			{selected === 'new' ? (
				<div className="pt-[4px]">
					<NewCardElement studentId={studentId} stripeAccountId={stripeAccountId} onBridge={onBridge} />
				</div>
			) : null}
		</div>
	);
};

export default PaymentMethodSection;
